import { useState } from 'react';
import { useSession } from '../../auth/SessionProvider';
import { deleteTask, saveTask, useBoard, useClients, useTasks } from '../../lib/data';
import { fromD, fromDT, toDT } from '../../lib/format';
import { PRIORITIES, TASK_STAGES, type Priority, type TaskStage } from '../../lib/types';
import { CloseX, OwnerOptions, useDrawer, useToast } from '../../ui/kit';

const tomorrow5pm = () => {
  const d = new Date(Date.now() + 86_400_000);
  d.setHours(17, 0, 0, 0);
  return d.toISOString();
};

export function TaskDrawer({
  id,
  clientId,
}: {
  id?: string | undefined;
  clientId?: string | undefined;
}) {
  const { me, can } = useSession();
  const { open, close } = useDrawer();
  const toast = useToast();
  const tasks = useTasks();
  const clients = useClients();
  const board = useBoard(can('pipeline'));
  const task = id ? tasks.data?.find((t) => t.id === id) : undefined;

  const [f, setF] = useState(() => ({
    text: task?.text ?? '',
    notes: task?.notes ?? '',
    client_id: task?.client_id ?? clientId ?? '',
    owner_id: task?.owner_id ?? me?.id ?? '',
    co_owner_id: task?.co_owner_id ?? '',
    priority: (task?.priority ?? 'normal') as Priority,
    stage: (task?.stage ?? 'todo') as TaskStage,
    due: toDT(task?.due_at ?? tomorrow5pm()),
  }));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  if (id && tasks.isLoading) {
    return (
      <div className="dhead">
        <h2 id="dtitle">Loading…</h2>
        <CloseX />
      </div>
    );
  }
  if (id && !task) {
    return (
      <div className="dhead">
        <h2 id="dtitle">Task not found</h2>
        <CloseX />
      </div>
    );
  }

  const lead = task?.lead_id ? board.data?.find((l) => l.id === task.lead_id) : undefined;
  const mine = !task || task.created_by === me?.id;
  const set =
    (k: keyof typeof f) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setF({ ...f, [k]: e.target.value });

  async function save() {
    const due = fromDT(f.due) ?? fromD(f.due.slice(0, 10));
    if (!f.text.trim()) return setErr('Add a title.');
    if (!due) return setErr('Add a deadline.');
    setBusy(true);
    setErr('');
    try {
      await saveTask(task?.id ?? null, {
        text: f.text.trim(),
        notes: f.notes,
        client_id: f.client_id || null,
        owner_id: f.owner_id || null,
        co_owner_id: f.co_owner_id || null,
        priority: f.priority,
        stage: f.stage,
        due_at: due,
      });
      toast(task ? 'Task saved' : 'Task added');
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!task || !window.confirm('Delete this task?')) return;
    setBusy(true);
    try {
      await deleteTask(task.id);
      toast('Task deleted');
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const client = clients.data?.find((c) => c.id === f.client_id);
  const shared = Boolean(f.owner_id && f.owner_id !== me?.id);

  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">{task ? 'Task' : 'New task'}</h2>
          <div className="sm">
            {shared
              ? 'Assigned to someone else: you both see it.'
              : client
                ? `Filed under ${client.name}: its members see it too.`
                : 'Private: only you see it.'}
          </div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="panel grid2">
          <label className="field full">
            <span>Title</span>
            <input type="text" value={f.text} onChange={set('text')} autoFocus={!task} />
          </label>
          <label className="field">
            <span>Client</span>
            <select aria-label="Client" value={f.client_id} onChange={set('client_id')}>
              <option value="">No client</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Assigned to</span>
            <select aria-label="Assigned to" value={f.owner_id} onChange={set('owner_id')}>
              <OwnerOptions />
            </select>
          </label>
          <label className="field">
            <span>Together with</span>
            <select aria-label="Together with" value={f.co_owner_id} onChange={set('co_owner_id')}>
              <option value="">Nobody</option>
              <OwnerOptions />
            </select>
          </label>
          <label className="field">
            <span>Priority</span>
            <select aria-label="Priority" value={f.priority} onChange={set('priority')}>
              {PRIORITIES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Stage</span>
            <select aria-label="Stage" value={f.stage} onChange={set('stage')}>
              {TASK_STAGES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Deadline</span>
            <input type="datetime-local" value={f.due} onChange={set('due')} />
          </label>
          <label className="field full">
            <span>Notes</span>
            <textarea rows={5} value={f.notes} onChange={set('notes')} />
          </label>
        </div>
        {lead && (
          <button className="btn" onClick={() => open({ type: 'lead', id: lead.id })}>
            Open {lead.clinic}
          </button>
        )}
      </div>
      <div className="dfoot">
        {err && (
          <p className="err" role="alert">
            {err}
          </p>
        )}
        <button className="btn primary big" onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : task ? 'Save' : 'Add task'}
        </button>
        {task && mine && (
          <button className="btn ghost" onClick={() => void remove()} disabled={busy}>
            Delete
          </button>
        )}
        <button className="btn ghost" onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
